import type { TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, TextButtonCell } from '~/components/common';
import { ReceiveSupplierCell, ReceiveSupplierHeader } from '~/components/receive';

import { disableInE2E, formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT, RELEASE_CODE, type TypeReceiveRequestStatus } from '~/constants';

import { formatReceiveExpectedDateTime } from '../formatReceiveExpectedDateTime';
import { RECEIVE_REQUEST_STATUS_COLOR_MAP } from './receiveBadgeColorMaps';

import type { TypeReceiveRequestsInfo } from '~/types/receive.types';

/**
 * [입고관리 > 입고요청관리] 페이지 테이블
 */
const getReceiveRegisterColumnDefs = ({
  onClickViewDetail,
}: {
  onClickViewDetail: (data?: TypeReceiveRequestsInfo) => void;
}) => {
  const columnDefs: TypeTableColumn<TypeReceiveRequestsInfo>[] = [
    {
      id: 'no',
      header: 'No',
      cell: ({ row }) => row.index + 1,
      size: 72,
      meta: { selection: true },
    },
    {
      accessorKey: 'requestCode',
      header: '입고요청번호',
      size: 180,
    },
    {
      accessorKey: 'status',
      header: '요청상태',
      size: 100,
      minSize: 100,
      cell: ({ getValue }) => (
        <BadgeCell
          value={getValue<TypeReceiveRequestStatus>()}
          colorMap={RECEIVE_REQUEST_STATUS_COLOR_MAP}
          variant="light"
          status
        />
      ),
    },
    {
      accessorKey: 'warehouseName',
      header: '입고 센터',
      size: 120,
    },
    {
      id: 'xdockYn',
      accessorFn: (data) => (data.xdockYn === true ? '이용함' : '이용안함'),
      header: '입고대행서비스',
      size: 110,
    },
    {
      id: 'releaseCode',
      accessorFn: (data) => RELEASE_CODE[data.releaseCode as keyof typeof RELEASE_CODE] ?? '-',
      header: '입고 방법',
      size: 90,
    },
    {
      id: 'receivingExpectedDate',
      accessorFn: (data) => formatReceiveExpectedDateTime(data.receivingExpectedDate, data.receivingExpectedTime),
      header: '입고예정일시',
      size: 200,
    },
    {
      id: 'viewDetail',
      header: '',
      size: 80,
      minSize: 80,
      cell: ({ row }) => (
        <TextButtonCell showDashIfNoValue={false} onClick={() => onClickViewDetail(row.original)}>
          상세보기
        </TextButtonCell>
      ),
    },
    {
      id: 'supplier',
      header: () => <ReceiveSupplierHeader />,
      cell: ({ row }) => <ReceiveSupplierCell data={row.original} />,
      minSize: 240,
      meta: { flex: disableInE2E(true) },
    },
    {
      accessorKey: 'skuCnt',
      header: '품목',
      cell: ({ getValue }) => {
        const value = getValue<TypeReceiveRequestsInfo['skuCnt']>();
        return value ? formatNumber(value) : '-';
      },
      size: 80,
      meta: { cellStyle: { textAlign: 'right' } },
    },
    {
      accessorKey: 'unitQty',
      header: '총 수량',
      cell: ({ getValue }) => {
        const value = getValue<TypeReceiveRequestsInfo['unitQty']>();
        return value ? formatNumber(value) : '-';
      },
      size: 80,
      meta: { cellStyle: { textAlign: 'right' } },
    },
    {
      accessorKey: 'requestedDate',
      header: '요청등록일시',
      cell: ({ getValue }) => formatDate(getValue<string>(), DATE_FORMAT.DATE_TIME) ?? '-',
      size: 160,
    },
  ];

  return { columnDefs };
};

export default getReceiveRegisterColumnDefs;
